/**
 * ComposeWindowPage
 *
 * Standalone full-screen compose UI rendered at /compose-window.
 * Opened by PopoutComposeWindow via:
 *
 *   window.open('/compose-window?key=bpa_compose_1234', ...)
 *
 * The parent window stores compose data in localStorage under that key.
 * This page reads + removes the key, then renders the full compose UI
 * (ComposeRibbon + ComposeMail) with no app shell around it.
 *
 * Because this is a real page load in a new window, Quill, Ant Design and
 * every other module start fresh in the correct window context — no
 * cross-window rendering quirks.
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setInlineComposeSession, clearInlineCompose } from '../../../Slices/mailUiSlice';
import ComposeRibbon from './ComposeRibbon';
import ComposeMail   from './ComposeMail';

export default function ComposeWindowPage() {
  const dispatch = useDispatch();

  // ── Read compose data from localStorage ─────────────────────────────────
  const { composeType, initialValues } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const key    = params.get('key');
    if (!key) return { composeType: 'new', initialValues: {} };
    try {
      const raw = localStorage.getItem(key);
      if (raw) localStorage.removeItem(key); // one-time read — clean up
      const parsed = raw ? JSON.parse(raw) : {};
      return {
        composeType:   parsed.composeType   || 'new',
        initialValues: parsed.initialValues || {},
      };
    } catch {
      return { composeType: 'new', initialValues: {} };
    }
  }, []);

  // ── Activate compose mode in this window's Redux store ──────────────────
  // (each browser window has its own store — this doesn't affect the parent)
  const activated = useRef(false);
  useEffect(() => {
    if (activated.current) return;
    activated.current = true;
    dispatch(setInlineComposeSession(`popout_${Date.now()}`));
    return () => { dispatch(clearInlineCompose()); };
  }, [dispatch]);

  // ── Compose options (Options ribbon tab) ─────────────────────────────────
  const [composeOptions, setComposeOptions] = useState({
    showBcc:                false,
    requestReadReceipt:     false,
    requestDeliveryReceipt: false,
    importance:             'normal',
    sensitivity:            'normal',
  });

  const handleClose = () => window.close();

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{
      width: '100vw', height: '100vh',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden', background: '#ffffff',
      fontFamily: "'Segoe UI', Tahoma, sans-serif",
    }}>
      {/* Full ribbon — Message / Insert / Format Text / Options */}
      <ComposeRibbon
        composeOptions={composeOptions}
        onOptionsChange={setComposeOptions}
      />

      {/* Compose form — action bar + fields + Quill editor */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <ComposeMail
          composeType={composeType}
          initialValues={initialValues}
          onClose={handleClose}
          onSent={handleClose}
          composeOptions={composeOptions}
          /* no onPopout — already in a popup window */
        />
      </div>
    </div>
  );
}
